/**
 * SRS (Spaced Repetition System) Manager
 * Zarządza systemem inteligentnych powtórek w localStorage
 */

export class SRSManager {
  constructor() {
    this.storageKey = 'german_learning_srs';
    this.initializeStorage();
  }

  // Poziomy opóźnień w godzinach
  static DELAY_LEVELS = {
    0: { name: "Nowe", delay_hours: 0 },
    1: { name: "Początkowe", delay_hours: 3 },
    2: { name: "Zaawansowane", delay_hours: 24 },
    3: { name: "Opanowane", delay_hours: 168 } // 1 tydzień
  };

  // Nowe progi czasu reakcji
  static REACTION_TIME_THRESHOLDS = {
    FAST: Infinity,      // < 5s
    OKAY: Infinity,     // < 10s
    POOR: Infinity   // >= 10s
  };

  /**
   * Inicjalizuje strukturę danych w localStorage jeśli nie istnieje
   */
  initializeStorage() {
    const existingData = localStorage.getItem(this.storageKey);
    if (!existingData) {
      const initialData = {
        sentences: {},
        words: {},
        stats: {
          total_reviews: 0,
          daily_streak: 0,
          last_session: null
        }
      };
      this.saveData(initialData);
    }
  }

  /**
   * Ładuje wszystkie dane SRS z localStorage
   */
  loadData() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : {};
    } catch (error) {
      console.error('Error loading SRS data:', error);
      return {};
    }
  }

  /**
   * Zapisuje dane SRS do localStorage
   */
  saveData(data) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(data));
      return true;
    } catch (error) {
      console.error('Error saving SRS data:', error);
      return false;
    }
  }

  /**
   * Tworzy nowy rekord dla elementu
   */
  createNewItem() {
    const now = new Date().toISOString();
    return {
      next_review: now, // Natychmiast dostępne do powtórki
      level: 0,
      review_count: 0,
      last_reaction_time: null,
      created_at: now,
      last_reviewed: null
    };
  }

  /**
   * Generuje klucz dla elementu
   */
  getItemKey(category, poolCode, itemIndex) {
    return `${category}.${poolCode}.${itemIndex}`;
  }

  /**
   * Oblicza następną datę powtórki na podstawie poziomu
   */
  calculateNextReview(level) {
    const delayHours = SRSManager.DELAY_LEVELS[level]?.delay_hours || 0;
    const nextReview = new Date();
    nextReview.setHours(nextReview.getHours() + delayHours);
    return nextReview.toISOString();
  }

  /**
   * Ocenia czas reakcji i zwraca dostępne opcje
   */
  evaluateReactionTime(reactionTime) {
    if (reactionTime < SRSManager.REACTION_TIME_THRESHOLDS.FAST) {
      // < 5s: wszystkie opcje
      return {
        quality: 'FAST',
        message: 'Bardzo szybko! Wybierz czas powtórki.',
        buttons: [
          { text: "Popełniłem błąd", action: "mistake", target_level: 0, color: "red", size: "large" },
          { text: "Wiedziałem, schowaj na 3h", action: "correct_3h", target_level: 1, color: "green", size: "medium" },
          { text: "Wiedziałem, schowaj na 24h", action: "correct_24h", target_level: 2, color: "blue", size: "medium" },
          { text: "Wiedziałem, powtórz za 2 dni", action: "correct_2days", target_level: 2, color: "indigo", size: "medium" },
          { text: "Wiedziałem, powtórz za 3 dni", action: "correct_3days", target_level: 2, color: "cyan", size: "medium" },
          { text: "Opanowane (tydzień)", action: "correct_week", target_level: 3, color: "purple", size: "medium" },
          { text: "Znam to, nie powtarzaj więcej", action: "no_repeat", target_level: 3, color: "orange", size: "medium" }
        ]
      };
    } else if (reactionTime < SRSManager.REACTION_TIME_THRESHOLDS.OKAY) {
      // 5-10s: tylko 3h, błąd
      return {
        quality: 'OKAY',
        message: 'Nieźle, ale można szybciej.',
        buttons: [
          { text: "Popełniłem błąd", action: "mistake", target_level: 0, color: "red", size: "large" },
          { text: "Wiedziałem, schowaj na 3h", action: "correct_3h", target_level: 1, color: "green", size: "medium" }
        ]
      };
    } else {
      // >= 10s: tylko błąd (auto_poor)
      return {
        quality: 'POOR',
        message: 'Za długo. Spróbuj ponownie za chwilę.',
        buttons: [] // Brak opcji, automatycznie zostaje na tym samym poziomie
      };
    }
  }

  /**
   * Przetwarza akcję użytkownika i aktualizuje element
   */
  processUserAction(item, action, reactionTime) {
    const now = new Date().toISOString();
    item.last_reviewed = now;
    item.last_reaction_time = reactionTime;
    item.review_count += 1;

    switch (action) {
      case 'mistake':
        item.level = 0;
        // Ustaw next_review na teraz - element będzie dostępny natychmiast w następnej sesji
        item.next_review = this.calculateNextReview(0);
        break;
      case 'correct':
      case 'correct_3h':
        item.level = Math.min(item.level + 1, 1);
        item.next_review = this.calculateNextReview(1);
        break;
      case 'correct_24h':
        item.level = Math.min(item.level + 1, 2);
        item.next_review = this.calculateNextReview(2);
        break;
      case 'correct_2days': {
        item.level = Math.min(item.level + 1, 2);
        const nextReview2d = new Date();
        nextReview2d.setHours(nextReview2d.getHours() + 48); // 2 dni
        item.next_review = nextReview2d.toISOString();
        break;
      }
      case 'correct_3days': {
        item.level = Math.min(item.level + 1, 2);
        const nextReview3d = new Date();
        nextReview3d.setHours(nextReview3d.getHours() + 72); // 3 dni
        item.next_review = nextReview3d.toISOString();
        break;
      }
      case 'correct_week':
        item.level = 3; // Opanowane - zawsze poziom 3
        item.next_review = this.calculateNextReview(3);
        break;
      case 'no_repeat':
        item.level = 3; // Zostajemy na poziomie 3
        // Ustaw next_review na rok w przód
        const nextReview = new Date();
        nextReview.setFullYear(nextReview.getFullYear() + 1);
        item.next_review = nextReview.toISOString();
        break;
      case 'auto_poor':
        // Pozostaje na tym samym poziomie, next_review na teraz
        item.next_review = this.calculateNextReview(item.level);
        break;
      default:
        console.warn('Unknown action:', action);
    }

    return item;
  }

  /**
   * Aktualizuje element w systemie SRS
   */
  updateItem(category, poolCode, itemIndex, reactionTime, userAction) {
    const data = this.loadData();
    
    // Inicjalizacja struktury jeśli nie istnieje
    if (!data[category]) data[category] = {};
    if (!data[category][poolCode]) data[category][poolCode] = {};
    if (!data[category][poolCode][itemIndex]) {
      data[category][poolCode][itemIndex] = this.createNewItem();
    }
    
    const item = data[category][poolCode][itemIndex];
    
    // Przetwórz akcję użytkownika
    this.processUserAction(item, userAction, reactionTime);
    
    // Aktualizuj statystyki globalne
    data.stats.total_reviews += 1;
    data.stats.last_session = new Date().toISOString();
    
    // Zapisz dane
    this.saveData(data);
    
    return item;
  }

  /**
   * Pobiera element z systemu SRS
   */
  getItem(category, poolCode, itemIndex) {
    const data = this.loadData();
    return data[category]?.[poolCode]?.[itemIndex] || null;
  }

  /**
   * Pobiera elementy gotowe do powtórki
   */
  getItemsDueForReview(category, poolCodes = null) {
    const now = new Date().toISOString();
    const data = this.loadData();
    
    if (!data[category]) return [];
    
    const dueItems = [];
    const pools = poolCodes ? poolCodes : Object.keys(data[category]);
    
    pools.forEach(poolCode => {
      if (data[category][poolCode]) {
        Object.entries(data[category][poolCode]).forEach(([itemIndex, item]) => {
          if (!item.next_review || item.next_review <= now) {
            dueItems.push({
              category,
              poolCode,
              itemIndex: parseInt(itemIndex),
              ...item
            });
          }
        });
      }
    });
    
    // Sortuj według poziomu (najniższe pierwsze) i czasu ostatniej powtórki
    return dueItems.sort((a, b) => {
      if (a.level !== b.level) {
        return a.level - b.level;
      }
      return (a.last_reviewed || a.created_at).localeCompare(b.last_reviewed || b.created_at);
    });
  }

  /**
   * Pobiera statystyki dla kategorii/puli
   */
  getStats(category = null, poolCode = null) {
    const data = this.loadData();
    
    if (!category) {
      return data.stats;
    }
    
    const categoryData = data[category] || {};
    const pools = poolCode ? [poolCode] : Object.keys(categoryData);
    
    let stats = {
      total_items: 0,
      by_level: { 0: 0, 1: 0, 2: 0, 3: 0 }, // 4-poziomowy system
      due_for_review: 0,
      avg_reaction_time: 0
    };
    
    const now = new Date().toISOString();
    let totalReactionTime = 0;
    let reactionCount = 0;
    
    pools.forEach(pool => {
      if (categoryData[pool]) {
        Object.values(categoryData[pool]).forEach(item => {
          stats.total_items += 1;
          // Zlicz tylko poziomy 0-3, ignoruj stare poziomy 4-5
          if (item.level >= 0 && item.level <= 3) {
            stats.by_level[item.level] += 1;
          }
          
          if (!item.next_review || item.next_review <= now) {
            stats.due_for_review += 1;
          }
          
          if (item.last_reaction_time) {
            totalReactionTime += item.last_reaction_time;
            reactionCount += 1;
          }
        });
      }
    });
    
    if (reactionCount > 0) {
      stats.avg_reaction_time = Math.round(totalReactionTime / reactionCount);
    }
    
    return stats;
  }

  /**
   * Resetuje dane dla kategorii/puli
   */
  resetData(category = null, poolCode = null) {
    const data = this.loadData();
    
    if (!category) {
      // Reset wszystkich danych
      this.initializeStorage();
      return true;
    }
    
    if (!poolCode) {
      // Reset całej kategorii
      data[category] = {};
    } else {
      // Reset konkretnej puli
      if (data[category]) {
        data[category][poolCode] = {};
      }
    }
    
    return this.saveData(data);
  }

  /**
   * Generuje inteligentną sesję uczenia - mix powtórek i nowych elementów
   */
  generateSmartSession(category, poolCode, totalItems, targetSessionSize = 10) {
    const data = this.loadData();
    const poolData = data[category]?.[poolCode] || {};
    const dueItems = this.getItemsDueForReview(category, [poolCode]);
    
    // Priorytet: Elementy do powtórki, potem nowe elementy
    let session = [];
    
    // Dodaj elementy do powtórki (wysoki priorytet)
    dueItems.forEach(dueItem => {
      if (session.length < targetSessionSize) {
        const originalItem = totalItems.find(item => 
          item.id === dueItem.itemIndex || 
          totalItems.indexOf(item) === dueItem.itemIndex
        );
        
        if (originalItem) {
          session.push({
            id: dueItem.itemIndex,
            type: 'review',
            item: originalItem,
            srsData: dueItem
          });
        }
      }
    });
    
    // Wypełnij pozostałe miejsca nowymi elementami
    const remainingSlots = targetSessionSize - session.length;
    if (remainingSlots > 0) {
      const newItems = totalItems.filter((item, index) => {
        const srsItem = poolData[index];
        // Element jest nowy jeśli nie ma danych SRS lub poziom = 0
        return !srsItem || srsItem.level === 0;
      }).slice(0, remainingSlots);
      
      newItems.forEach((item, originalIndex) => {
        const itemIndex = totalItems.indexOf(item);
        session.push({
          id: itemIndex,
          type: 'new',
          item: item,
          srsData: poolData[itemIndex] || null
        });
      });
    }
    
    // Zwróć sesję bez mieszania - będzie przemieszana w komponencie jeśli potrzeba
    return session;
  }

  /**
   * Pomaga przemieszać tablicę
   */
  shuffleArray(array) {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  /**
   * Pobiera rekomendowany rozmiar sesji na podstawie statystyk puli
   */
  getRecommendedSessionSize(category, poolCode) {
    const stats = this.getStats(category, poolCode);
    const dueCount = stats.due_for_review;
    
    // Bazuj rozmiar sesji na elementach do powtórki + trochę nowych
    if (dueCount >= 15) return 20;
    if (dueCount >= 10) return 15;
    if (dueCount >= 5) return 10;
    return Math.max(5, dueCount + 3); // Minimum 5, ale dostosuj dla małych pul
  }

  /**
   * Sprawdza czy element jest gotowy do powtórki
   */
  isItemDueForReview(item) {
    if (!item.next_review) return true;
    return item.next_review <= new Date().toISOString();
  }

  /**
   * Pobiera wszystkie elementy z konkretnej puli
   */
  getPoolItems(category, poolCode) {
    const data = this.loadData();
    return data[category]?.[poolCode] || {};
  }

  /**
   * Pobiera statystyki dla konkretnej puli
   * @param {string} category - Kategoria (sentences, words)
   * @param {string} poolCode - Kod puli (np. A1-01-01)
   * @param {number} totalItemsInPool - Rzeczywista liczba elementów w puli (z pliku JSON)
   */
  getPoolStats(category, poolCode, totalItemsInPool = null) {
    const items = this.getPoolItems(category, poolCode);
    const now = new Date();
    const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000); // Za 24h
    
    const stats = {
      total: 0,
      dueForReview: 0,    // Nowe + z przekroczonym czasem
      tomorrow: 0,        // Z opóźnieniem < 24h
      mastered: 0         // Z opóźnieniem >= 24h (poziom 3)
    };

    // Zlicz elementy które mają dane SRS
    Object.values(items).forEach(item => {
      stats.total++;
      
      const nextReview = item.next_review ? new Date(item.next_review) : now;
      
      // Do powtórki: wszystkie z next_review <= teraz
      if (nextReview <= now) {
        stats.dueForReview++;
      }
      // Na jutro: next_review w przedziale (teraz, teraz+24h)
      else if (nextReview > now && nextReview <= tomorrow) {
        stats.tomorrow++;
      }
      // Opanowane: next_review > teraz+24h (poziom 3)
      else if (nextReview > tomorrow) {
        stats.mastered++;
      }
    });

    // Jeśli znamy rzeczywistą liczbę elementów w puli, dodaj brakujące jako "nowe"
    if (totalItemsInPool !== null && totalItemsInPool > stats.total) {
      const missingItems = totalItemsInPool - stats.total;
      stats.total = totalItemsInPool;
      stats.dueForReview += missingItems; // Nowe elementy są dostępne do nauki
    }

    return stats;
  }

  /**
   * Pobiera wszystkie zdania z puli dostępne do nauki (bez opóźnienia SRS)
   */
  getAvailableSentencesForPool(category, poolCode, allSentences) {
    const data = this.loadData();
    const poolData = data[category]?.[poolCode] || {};
    const now = new Date().toISOString();
    
    const availableSentences = [];
    
    allSentences.forEach((sentence, index) => {
      const srsData = poolData[index];
      
      // Element jest dostępny do nauki TYLKO jeśli:
      // 1. Nie ma danych SRS (nowe słowo) LUB
      // 2. Ma dane SRS i next_review jest w przeszłości lub równe teraz (zaległa powtórka)
      const isNew = !srsData;
      const isOverdue = srsData && (!srsData.next_review || srsData.next_review <= now);
      
      if (isNew || isOverdue) {
        availableSentences.push({
          id: index,
          type: isNew ? 'new' : 'review',
          item: sentence,
          srsData: srsData || null
        });
      }
    });
    
    // Zwróć dostępne elementy bez sortowania - będą tasowane w komponencie
    return availableSentences;
  }

  /**
   * Inicjalizuje wszystkie elementy puli w SRS przy pierwszym uruchomieniu
   * Tworzy rekordy SRS dla elementów które jeszcze nie istnieją
   * @param {string} category - Kategoria (sentences, words)
   * @param {string} poolCode - Kod puli
   * @param {Array} items - Wszystkie elementy puli
   * @returns {number} - Liczba nowo zainicjalizowanych elementów
   */
  initializePoolItems(category, poolCode, items) {
    const data = this.loadData();
    
    // Inicjalizacja struktury jeśli nie istnieje
    if (!data[category]) data[category] = {};
    if (!data[category][poolCode]) data[category][poolCode] = {};
    
    let newItemsCount = 0;
    
    items.forEach((item, index) => {
      // Tylko twórz rekord jeśli jeszcze nie istnieje
      if (!data[category][poolCode][index]) {
        data[category][poolCode][index] = this.createNewItem();
        newItemsCount++;
      }
    });
    
    // Zapisz tylko jeśli były nowe elementy
    if (newItemsCount > 0) {
      this.saveData(data);
      console.log(`SRS: Zainicjalizowano ${newItemsCount} nowych elementów dla ${category}/${poolCode}`);
    }
    
    return newItemsCount;
  }
}

// Singleton instance
export const srsManager = new SRSManager();
